/**
 * Centralized Error Handler
 *
 * Provides consistent error handling across the application
 * with proper logging, user notification, and recovery strategies.
 */

import logger from './logger';
import { AxiosError } from 'axios';

export interface ErrorDetails {
  message: string;
  code?: string;
  status?: number;
  timestamp?: string;
  path?: string;
  details?: unknown;
}

export class AppError extends Error {
  public readonly code?: string;
  public readonly status?: number;
  public readonly details?: unknown;
  public readonly timestamp: string;

  constructor(message: string, code?: string, status?: number, details?: unknown) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.timestamp = new Date().toISOString();

    // Maintains proper stack trace
    Error.captureStackTrace(this, AppError);
  }

  toJSON(): ErrorDetails {
    return {
      message: this.message,
      code: this.code,
      status: this.status,
      timestamp: this.timestamp,
      details: this.details,
    };
  }
}

/**
 * Extract error details from various error types
 */
export const extractErrorDetails = (error: unknown): ErrorDetails => {
  // Handle Axios errors
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<any>;

    if (axiosError.response) {
      // Server responded with error
      const { data, status } = axiosError.response;
      return {
        message: data?.message || data?.error || axiosError.message,
        code: data?.code || 'SERVER_ERROR',
        status,
        details: data,
      };
    } else if (axiosError.request) {
      // Request was made but no response
      return {
        message: '서버와 통신할 수 없습니다. 네트워크를 확인해주세요.',
        code: 'NETWORK_ERROR',
        status: 0,
      };
    } else {
      // Request setup error
      return {
        message: axiosError.message,
        code: 'REQUEST_ERROR',
      };
    }
  }

  // Handle AppError
  if (error instanceof AppError) {
    return error.toJSON();
  }

  // Handle standard Error
  if (error instanceof Error) {
    return {
      message: error.message,
      code: error.name,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
      code: 'STRING_ERROR',
    };
  }

  // Handle unknown errors
  return {
    message: '알 수 없는 오류가 발생했습니다.',
    code: 'UNKNOWN_ERROR',
    details: error,
  };
};

/**
 * Get user-friendly error message
 */
export const getUserMessage = (error: unknown): string => {
  const details = extractErrorDetails(error);

  // Map common error codes to user-friendly messages
  const userMessages: Record<string, string> = {
    NETWORK_ERROR: '네트워크 연결을 확인해주세요.',
    UNAUTHORIZED: '로그인이 필요합니다.',
    FORBIDDEN: '접근 권한이 없습니다.',
    NOT_FOUND: '요청한 리소스를 찾을 수 없습니다.',
    VALIDATION_ERROR: '입력값을 확인해주세요.',
    SERVER_ERROR: '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
  };

  // Check for specific status codes
  if (details.status === 401) {
    return userMessages.UNAUTHORIZED;
  } else if (details.status === 403) {
    return userMessages.FORBIDDEN;
  } else if (details.status === 404) {
    return userMessages.NOT_FOUND;
  } else if (details.status && details.status >= 500) {
    return userMessages.SERVER_ERROR;
  }

  // Check for error code
  if (details.code && userMessages[details.code]) {
    return userMessages[details.code];
  }

  // Return the actual message or default
  return details.message || '오류가 발생했습니다.';
};

/**
 * Log error with context
 */
export const logError = (
  error: unknown,
  context?: string,
  additionalInfo?: Record<string, unknown>
): void => {
  const details = extractErrorDetails(error);

  logger.error(`[${context || 'Error'}]`, {
    ...details,
    ...additionalInfo,
    stack: error instanceof Error ? error.stack : undefined,
  });
};

/**
 * Handle error with logging and optional rethrow
 */
export const handleError = (
  error: unknown,
  context?: string,
  options?: {
    rethrow?: boolean;
    fallbackMessage?: string;
    additionalInfo?: Record<string, unknown>;
  }
): void => {
  const { rethrow = true, fallbackMessage, additionalInfo } = options || {};

  // Log the error
  logError(error, context, additionalInfo);

  // Rethrow if needed
  if (rethrow) {
    if (error instanceof AppError) {
      throw error;
    } else {
      const details = extractErrorDetails(error);
      throw new AppError(
        fallbackMessage || details.message,
        details.code,
        details.status,
        details.details
      );
    }
  }
};

/**
 * Create standardized service error handler
 */
export const createServiceErrorHandler = (serviceName: string) => {
  return (methodName: string) => {
    return (error: unknown): never => {
      handleError(error, `${serviceName}.${methodName}`, {
        rethrow: true,
        additionalInfo: {
          service: serviceName,
          method: methodName,
        },
      });
      // This line is never reached due to rethrow, but TypeScript needs it
      throw error;
    };
  };
};

/**
 * Async error wrapper for consistent error handling
 */
export const withErrorHandling = async <T>(
  operation: () => Promise<T>,
  context?: string,
  fallbackMessage?: string
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    handleError(error, context, { rethrow: true, fallbackMessage });
    // Never reached, but needed for TypeScript
    throw error;
  }
};